import { FC, useCallback, useMemo, useState } from 'react'
import { Space, Typography } from 'antd'
import * as Icons from '@ant-design/icons'
import _ from 'lodash'
import { useFormContext } from 'react-hook-form'

import { Select, LabeledValue } from 'admin/lib/Forms'
import { Queries, APITypes } from 'admin/lib/Api'
import { Waste } from 'admin/lib/Records'
import { Waste as WasteV2 } from 'admin/lib/Records/fields_v2'

export interface FacilityPresetSelectProps {
  setPermitPresentedTrue: any
  onChange: () => void
  disabled?: boolean
  hidden?: boolean
  enriched?: boolean
  instrumented?: boolean
}

type FacilityOption = LabeledValue & {
  facility: APITypes.WasteFacility
  address: string
}

export const FacilityPresetSelect: FC<FacilityPresetSelectProps> = ({
  setPermitPresentedTrue,
  onChange,
  disabled = false,
  hidden = false,
  enriched = false,
  instrumented = false
}) => {
  const form = useFormContext()
  const removalDate = form.getValues('data.removalDate')
  const [search, setSearch] = useState<string | null>(null)
  const query = Queries.useWasteFacilities(search, removalDate)

  const options = useMemo(() => {
    if (!query.data?.pages) {
      return []
    }

    return _(query.data.pages)
      .flatMap(page => page?.items ?? [])
      .flatMap<FacilityOption>(facility => {
        const addresses: string[] =
          facility.address.length === 0 ? [''] : facility.address

        return addresses.map(address => ({
          value: `${facility.source}:${facility.permitId}`,
          label: (
            <Space>
              <Typography.Text>
                {facility.operator} ({facility.permitId})
              </Typography.Text>

              <Typography.Text type="secondary">{address}</Typography.Text>
            </Space>
          ),
          facility,
          address
        }))
      })
      .value()
  }, [query.data?.pages])

  const deDuppedOptions = useMemo(() => {
    return _.uniqBy(options, 'value')
  }, [options])

  const handleSelect = useCallback(
    (label, value) => {
      const option = value as FacilityOption
      if (enriched) {
        form.setValue(
          WasteV2.enriched.facilityPermitId.path,
          option.facility.permitId
        )
        form.setValue(
          WasteV2.enriched.facilityOperatorName.path,
          option.facility.operator
        )
        form.setValue(WasteV2.enriched.facilitySiteAddress.path, option.address)
      } else {
        form.setValue(
          Waste.data.facilityPermitId.path,
          option.facility.permitId
        )
        form.setValue(
          Waste.data.facilityOperatorName.path,
          option.facility.operator
        )
        form.setValue(Waste.data.facilitySiteAddress.path, option.address)
        setPermitPresentedTrue()
      }

      if (onChange) {
        onChange()
      }
    },
    [enriched, form, onChange, setPermitPresentedTrue]
  )

  type HandleFocusEvent = React.FocusEvent<HTMLInputElement>
  const handleFocus = (e: HandleFocusEvent) => {
    if (instrumented) {
      performance.mark('field-entered', {
        detail: { fieldName: 'wasteDataFacilitySelector' }
      })
    }
  }

  type HandleBlurEvent = React.FocusEvent<HTMLInputElement>
  const handleBlur = (e: HandleBlurEvent) => {
    if (instrumented) {
      performance.mark('field-exited', {
        detail: { fieldName: 'wasteDataFacilitySelector' }
      })
      performance.measure('field-duration', {
        start: 'field-entered',
        end: 'field-exited',
        detail: { fieldName: 'wasteDataFacilitySelector' }
      })
    }
  }

  if (hidden) {
    return null
  }

  return (
    <Select<FacilityOption>
      id="wasteDataFacilitySelector"
      showSearch
      loading={query.isFetching}
      placeholder={query.isFetching ? 'Fetching' : 'Select a Facility to apply'}
      filter={false}
      options={deDuppedOptions}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onSearch={setSearch}
      onBottomReached={query.hasNextPage ? query.fetchNextPage : undefined}
      labelInValue
      onSelect={handleSelect}
      suffixIcon={<Icons.SearchOutlined />}
      disabled={disabled}
    />
  )
}
